import { X } from 'lucide-react';
import { useEffect } from 'react';
import toast, { type Toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/NewButton';
import { snoozeUpdate, startVersionWatcher } from '@/lib/appUpdate';

const UPDATE_TOAST_ID = 'app-update';

const UpdateToast = ({ toastInstance }: { toastInstance: Toast }) => {
  const { t } = useTranslation();

  const dismiss = () => {
    // Snooze so a long-lived session isn't re-prompted for the next 6h.
    snoozeUpdate();
    toast.dismiss(UPDATE_TOAST_ID);
  };

  return (
    <div
      className={`relative flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-[0_3px_10px_rgba(0,0,0,0.1),0_3px_3px_rgba(0,0,0,0.05)] transition-opacity duration-200 ${
        toastInstance.visible ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
    >
      <button
        type='button'
        aria-label={t('close')}
        onClick={dismiss}
        className='absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-700'
      >
        <X className='size-3' />
      </button>
      <span className='whitespace-nowrap text-sm text-gray-900'>{t('update-available')}</span>
      <Button size='sm' text={t('reload')} onClick={() => window.location.reload()} />
    </div>
  );
};

/**
 * Watches the live build version and, when a newer one ships, surfaces a
 * persistent toast inviting the user to reload. A soft prompt (rather than a
 * forced reload) avoids interrupting in-progress work such as open editors;
 * dismissing snoozes the prompt for 6h. Mounted only in production builds.
 */
export function AppUpdateWatcher() {
  useEffect(() => {
    return startVersionWatcher(() => {
      // toast.custom bypasses the shared ToastBar (no icon column / divider), but
      // that also means no built-in exit animation: a dismissed custom toast
      // otherwise lingers fully visible for react-hot-toast's ~1s removeDelay.
      // Use the render-prop form to fade out on `visible`, and shorten
      // removeDelay so it unmounts right after the fade.
      toast.custom((toastInstance) => <UpdateToast toastInstance={toastInstance} />, {
        id: UPDATE_TOAST_ID,
        duration: Number.POSITIVE_INFINITY,
        removeDelay: 250,
      });
    });
  }, []);

  return null;
}
